<template>
    <checkout-step>
        <template slot="title">
            <span class="uppercase"  @click="currentStep > 5 ? $parent.$parent.jumpTo(5) : null">
                {{ __('Összegzés') }}
            </span>
        </template>

        <div v-if="data">
            <portal-target
                v-if="portalAvailable"
                name="checkout-summary-before"
            />
            <div class="cart-content clear">

                <div id="cart-summary-wrap">

                    <div class="card-body">
                        <div>
                            <table>
                                <thead>
                                <tr>
                                    <th>{{ __('Termék neve') }}</th>
                                    <th>{{ __('Ár') }}</th>
                                    <th>{{ __('Kedvezményes ár') }}</th>
                                    <th>{{ __('Darab') }}</th>
                                    <th>{{ __('Összesen') }}</th>
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody>

                                <cart-item
                                    v-for="item in items"
                                    :key="item.id.identifier"
                                    :readonly="true"
                                    :item="item"
                                />

                                </tbody>
                                <tfoot>
                                <tr class="mobile-flex">
                                    <th class="mobile-flex-1" colspan="3">{{ __('Kosárban összesen') }}:</th>
                                    <td><span class="cart-qty">{{ totalItemQuantity }}</span> {{ __('Darab') }}</td>
                                    <td>
                                        <div class="cc-sum-val cart_sum">
                                            {{
                                                formatNegativePrice(price.gross) | currency(currencyFormat(items[0].currency))
                                            }}
                                        </div>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr v-if="shippable && data"
                                    class="cart-transport-cost mobile-flex">
                                    <th colspan="3" class="label mobile-flex-1">{{ __('Szállítási költség') }}:</th>
                                    <td><span class="price"></span></td>
                                    <td>
                                        <div class="cc-sum-val cart_sum">
                                            {{
                                                formatNegativePrice(shippingCost) | currency(currencyFormat(items[0].currency))
                                            }}
                                        </div>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr v-if="data && data.paymentFee > 0"
                                    class="cart-payment-cost mobile-flex">
                                    <th colspan="3" class="label mobile-flex-1">{{ data.paymentMode.summaryText }}:</th>
                                    <td></td>
                                    <td>
                                        <div class="cc-sum-val cart_sum">
                                            {{
                                                formatNegativePrice(paymentFee) | currency(currencyFormat(items[0].currency))
                                            }}
                                        </div>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr class="cart-total mobile-flex" id="cart-total-cost-rows">
                                    <th colspan="4" class="mobile-flex-1">{{ __('Összesen') }}:</th>
                                    <td>
                                        <span class="total_price">
                                            {{
                                                formatNegativePrice(price.gross + data.shippingCost + data.paymentFee) | currency(currencyFormat(items[0].currency))
                                            }}
                                        </span>
                                    </td>
                                    <td></td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="container px-0">
                <portal-target
                    v-if="portalAvailable"
                    name="summary-step-above-user-data"
                />
            </div>

            <div class="form-wrapper clear">
                <h4 class="title big">
                    {{ __('Személyes adatok') }}
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="form-col-3">
                        <h5 class="title pb-15">{{ __('Alap adatok') }}:</h5>
                        <div class="plain-text -mt-5" id="mainData">
                            {{ data.userData.name }}<br>
                            {{ data.userData.email }}<br>
                            {{ phoneNumber }}
                        </div>
                    </div>

                    <div class="form-col-3">
                        <h5 class="title pb-15">{{ __('Számlázási adatok') }}:</h5>
                        <div class="plain-text -mt-5" id="billingData">
                            <template v-if="data.userData.billing_name">{{ data.userData.billing_name }}<br></template>
                            {{ selectedCountry(data.userData.billing_country_id) }}<br>
                            {{ data.userData.billing_zip }}<br>
                            {{ data.userData.billing_city }}<br>
                            {{ data.userData.billing_address }} {{ data.userData.billing_house_number }}<br>
                            <span v-if="data.userData.vat_number">
                            {{ __('Adószám') }}: {{ data.userData.vat_number }}
                        </span>
                        </div>
                    </div>

                    <div class="form-col-3 last">
                        <h5 class="title pb-15">{{ __('Szállítási adatok') }}:</h5>
                        <div class="plain-text -mt-5" id="deliveryData">
                            <div v-if="selectedShippingModeHasPickupPoints">
                                <template v-if="data.userData.pickupPoint">
                                    {{ data.userData.pickupPoint.name }}<br>
                                    {{ data.userData.pickupPoint.zip }}<br>
                                    {{ data.userData.pickupPoint.city }}<br>
                                    {{ data.userData.pickupPoint.address }}<br>
                                </template>
                                <template v-else>
                                    {{ selectedPickupPointData.name }}<br>
                                    {{ selectedPickupPointData.zip }}<br>
                                    {{ selectedPickupPointData.city }}<br>
                                    {{ selectedPickupPointData.address }}<br>
                                </template>
                            </div>
                            <div v-else>
                                {{ data.userData.shipping_name || data.userData.billing_name || data.userData.name }}<br>
                                {{ selectedCountry(data.userData.shipping_country_id) }}<br>
                                {{ data.userData.shipping_zip || data.userData.billing_zip }}<br>
                                {{ data.userData.shipping_city || data.userData.billing_city }}<br>
                                {{ data.userData.shipping_address || data.userData.billing_address }}
                                {{ data.userData.shipping_house_number || data.userData.billing_house_number }}<br>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="form-col-3 clear">
                        <h5 class="title pb-15">{{ __('Fizetési mód') }}:</h5>
                        <div class="plain-text -mt-5" id="paymodeData">
                            {{ data.paymentMode ? data.paymentMode.name : '-' }}
                        </div>
                    </div>
                    <div class="form-col-3">
                        <h5 class="title pb-15">{{ __('Átvételi mód') }}:</h5>
                        <div class="plain-text -mt-5" id="receptionData">
                            {{ data.shippingMode.name }}
                        </div>
                    </div>

                </div>
                <portal-target
                    v-if="portalAvailable"
                    name="checkout-summary-after"
                />

                <form
                    ref="form"
                    method="post"
                    :action="route('shop.checkout.store')"
                >
                    <div class="form-row clear c-both">
                        <label for="o_message" class="italic">{{ __('Megjegyzés') }}:</label>
                        <textarea
                            id="comment"
                            name="comment"
                            class="form-control block w-full"
                        />
                    </div>
                    <div class="container p-0 text-right">
                        <portal-target
                            v-if="portalAvailable"
                            class="d-inline text-right"
                            name="summary-step-below-comment"
                        />
                    </div>
                    <div class="container p-0 flex mb-2">
                    <span class="pr-2">
                        <label class="checkbox bounce">
                            <input
                                id="acceptTerms"
                                v-model="acceptTerms"
                                type="checkbox"
                                name="acceptTerms"
                            >
                        </label>
                    </span>
                        <portal-target
                            v-if="portalAvailable"
                            class="d-inline text-right"
                            name="summary-step-accept-terms"
                        />
                    </div>
                    <div class="container p-0 flex mb-2">
                    <span class="pr-2">
                        <label class="checkbox bounce">
                            <input
                                id="sign-up-for-newsletter"
                                v-model="signUpForNewsletter"
                                type="checkbox"
                                name="signUpForNewsletter"
                            >
                        </label>
                    </span>
                        <portal-target
                            v-if="portalAvailable"
                            class="d-inline text-right"
                            name="sign-up-for-newsletter"
                        />
                    </div>

                    <div class="container p-0 flex mb-2" v-if="checkoutData && ! checkoutData.isAuthenticated">
                    <span class="pr-2">
                        <label class="checkbox bounce">
                            <input
                                id="create-user-after-purchase"
                                v-model="createUser"
                                type="checkbox"
                                name="createUser"
                            >
                        </label>
                    </span>
                        <portal-target
                            v-if="portalAvailable"
                            class="d-inline text-right"
                            name="create-user-after-purchase"
                        />
                    </div>
                    <div
                        v-if="!acceptTerms && triedToSubmit"
                        class="container p-0 text-right"
                    >
                    <span
                        class="alert-danger"
                        style="padding: 6px;border-radius: 6px;"
                    >
                        {{ __('A Vásárlási információkat és a Felhasználási és általános szerződési feltételeket el kell fogadni!') }}
                    </span>
                    </div>

                    <input
                        type="hidden"
                        name="_token"
                        :value="csrfToken()"
                    >
                </form>
            </div>
        </div>

        <template
            slot="navigation"
            slot-scope="{ prev, next }"
            style="padding: 0"
        >
            <button
                v-if="currentStep !== 1"
                type="button"
                class="cart-continue my-5"
                style="float:left;"
                @click="prev()"
            >
                {{ __('Vissza') }}
            </button>
            <button
                class="region-button my-5 btn--emphasized summary-step-submit-btn"
                :disabled="submitIsDisabled"
                @click="submitForm"
            >
                {{ __('Megrendelem') }}
            </button>
        </template>
    </checkout-step>
</template>

<script>
import summaryStepBase from '@webapix/shop-js/src/mixins/checkout/summaryStepBase'
import currencyFormatter from '../../../mixins/currencyFormatter';
import {mapState} from 'vuex';
import {sumBy} from 'lodash';

export default {
    mixins: [summaryStepBase, currencyFormatter],

    computed: {
        ...mapState('cart', [
            'items'
        ]),

        totalItemQuantity() {
            return sumBy(this.items, 'quantity')
        }
    },

    data() {
        return {
            signUpForNewsletter: false,
            createUser: false,
        }
    },

    methods: {
        selectedCountry(countryId) {
            return _.find(this.checkoutData.countries, ['id', countryId]).name
        }
    }
}
</script>

<style lang="scss">
button.summary-step-submit-btn {
    &:disabled {
        background-color: #7a7a7a !important;
        cursor: not-allowed;
    }
}
</style>
